import { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router-dom'
import { ApiError } from '../../lib/api'
import { loginWithPassword, requestMagicLink } from '../../lib/portal'

type Method = 'password' | 'magic-link'

export default function LoginPage() {
  const navigate = useNavigate()
  const [method, setMethod] = useState<Method>('password')

  return (
    <>
      <Helmet><title>Giriş — W.Sign</title><meta name="robots" content="noindex, nofollow" /></Helmet>
      <section className="container-page py-16 max-w-md">
        <h1 className="text-3xl font-semibold">Panele giriş</h1>

        <div className="mt-6 inline-flex rounded-md border border-[var(--color-border)] bg-[var(--color-bg-soft)] p-1">
          <button
            type="button"
            onClick={() => setMethod('password')}
            className={`rounded px-4 py-1.5 text-sm font-medium transition-colors ${
              method === 'password' ? 'bg-white text-[var(--color-fg)] shadow-sm' : 'text-[var(--color-fg-muted)]'
            }`}
          >
            E-posta + şifre
          </button>
          <button
            type="button"
            onClick={() => setMethod('magic-link')}
            className={`rounded px-4 py-1.5 text-sm font-medium transition-colors ${
              method === 'magic-link' ? 'bg-white text-[var(--color-fg)] shadow-sm' : 'text-[var(--color-fg-muted)]'
            }`}
          >
            E-posta bağlantısı
          </button>
        </div>

        <div className="mt-6">
          {method === 'password'
            ? <PasswordForm onSuccess={() => navigate('/dashboard')} onNoPassword={() => setMethod('magic-link')} />
            : <MagicLinkForm />}
        </div>
      </section>
    </>
  )
}

// ── Password login ──────────────────────────────────────────────────────────────

function PasswordForm({ onSuccess, onNoPassword }: { onSuccess: () => void; onNoPassword: () => void }) {
  const [email, setEmail]       = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy]         = useState(false)
  const [error, setError]       = useState<string | null>(null)

  const submit = async () => {
    if (!email.includes('@') || !password) { setError('E-posta ve şifre girin.'); return }
    setBusy(true); setError(null)
    try {
      await loginWithPassword(email, password)
      onSuccess()
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) setError('E-posta veya şifre hatalı.')
      else setError('Giriş yapılamadı. Daha sonra tekrar deneyin.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card">
      <label className="block">
        <span className="text-xs font-semibold uppercase tracking-wide text-[var(--color-fg-muted)]">E-posta</span>
        <input
          type="email" value={email} autoFocus placeholder="ad@firma.com"
          onChange={e => setEmail(e.target.value)}
          className="mt-1 block w-full rounded-md border border-[var(--color-border)] bg-white px-3 py-2 text-sm focus:border-[var(--color-accent)] focus:outline-none"
        />
      </label>
      <label className="mt-4 block">
        <span className="text-xs font-semibold uppercase tracking-wide text-[var(--color-fg-muted)]">Şifre</span>
        <input
          type="password" value={password} placeholder="••••••••"
          onChange={e => setPassword(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') void submit() }}
          className="mt-1 block w-full rounded-md border border-[var(--color-border)] bg-white px-3 py-2 text-sm focus:border-[var(--color-accent)] focus:outline-none"
        />
      </label>
      <button onClick={submit} disabled={busy} type="button" className="btn-primary mt-4 w-full">
        {busy ? 'Giriş yapılıyor…' : 'Giriş yap'}
      </button>
      {error && <p className="mt-3 text-sm text-red-700">{error}</p>}
      <button onClick={onNoPassword} type="button" className="btn-ghost mt-3 w-full text-xs">
        Şifren yok mu / unuttun mu? E-posta ile giriş bağlantısı al
      </button>
    </div>
  )
}

// ── Magic-link (passwordless) ─────────────────────────────────────────────────────

function MagicLinkForm() {
  const [email, setEmail] = useState('')
  const [sent, setSent]   = useState(false)
  const [busy, setBusy]   = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async () => {
    if (!email.includes('@')) { setError('Geçerli bir e-posta girin.'); return }
    setBusy(true); setError(null)
    try {
      await requestMagicLink(email)
      setSent(true)
    } catch {
      setError('Bağlantı gönderilemedi. Daha sonra tekrar deneyin.')
    } finally {
      setBusy(false)
    }
  }

  if (sent) {
    return (
      <div className="card border-emerald-200 bg-emerald-50">
        <p className="text-sm text-emerald-900">
          <strong>{email}</strong> adresine bir giriş bağlantısı gönderdik. Gelen kutunuzu kontrol edin —
          bağlantı 15 dakika geçerli. Giriş yaptıktan sonra <strong>Hesap</strong> sayfasından şifre belirleyebilirsiniz.
        </p>
        <button onClick={() => setSent(false)} type="button" className="btn-ghost mt-3 text-xs">
          Farklı e-posta dene
        </button>
      </div>
    )
  }

  return (
    <div className="card">
      <p className="text-sm text-[var(--color-fg-muted)]">
        E-postanızı girin; size tek kullanımlık bir giriş bağlantısı gönderelim. Şifre gerekmez.
      </p>
      <label className="mt-4 block">
        <span className="text-xs font-semibold uppercase tracking-wide text-[var(--color-fg-muted)]">E-posta</span>
        <input
          type="email" value={email} autoFocus placeholder="ad@firma.com"
          onChange={e => setEmail(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') void submit() }}
          className="mt-1 block w-full rounded-md border border-[var(--color-border)] bg-white px-3 py-2 text-sm focus:border-[var(--color-accent)] focus:outline-none"
        />
      </label>
      <button onClick={submit} disabled={busy} type="button" className="btn-primary mt-4 w-full">
        {busy ? 'Gönderiliyor…' : 'Giriş bağlantısı gönder'}
      </button>
      {error && <p className="mt-3 text-sm text-red-700">{error}</p>}
    </div>
  )
}
